import { audio } from '../systems/audio/AudioSystem.js';
import { BOSSES } from '../data/bosses.js';
import { CHALLENGE_BOSSES } from '../data/challengeBosses.js';
import { CHARACTERS } from '../data/characters.js';
import { ACT2_BOSSES } from '../data/act2Bosses.js';
import { ACT3_BOSSES } from '../data/act3Bosses.js';
import { PROLOGUE, PROLOGUE_II, PROLOGUE_III, EPILOGUE, EPILOGUE_II, EPILOGUE_III, BOSS_LORE, CHAMPION_LORE } from '../data/lore.js';

/**
 * The Unmade Archive — the story codex. Reached from the Settings & Records
 * hub (ui/SettingsHubMenu.js). Mirrors the records-screen pattern
 * (StatisticsMenu.js): constructor(root, save, { onBack }), an .menu-overlay
 * with a scrollable body, destroy().
 *
 * Progressive unlock (see data/lore.js + SaveManager.recordBossDefeated):
 * boss entries stay sealed until the boss is defeated — gauntlet bosses also
 * unlock retroactively via highestGauntletIndex, so a returning player sees
 * the lore they've already earned. Champions and the prologue are always
 * open; the epilogue opens on a first clear.
 */
export class LoreMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay lore-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _isBossUnlocked(spriteKey, gauntletIndex = -1) {
    const stats = this.save.statistics;
    const defeated = stats.defeatedBosses ?? [];
    if (defeated.includes(spriteKey)) return true;
    // Gauntlet bosses (gauntletIndex >= 0) also unlock retroactively once the
    // player has reached at least that deep, so existing saves aren't blank.
    return gauntletIndex >= 0 && stats.highestGauntletIndex >= gauntletIndex;
  }

  _render() {
    const scrollPosition = this.el.querySelector('.lore-body')?.scrollTop ?? 0;
    this.el.innerHTML = '';

    const kicker = document.createElement('div');
    kicker.className = 'menu-kicker';
    kicker.textContent = 'THE UNMADE GAUNTLET';
    this.el.appendChild(kicker);
    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'THE ARCHIVE';
    this.el.appendChild(title);

    const body = document.createElement('div');
    body.className = 'lore-body';

    // Two different unlock bars run through this screen, and the difference
    // matters. A boss ENTRY opens on having beaten that boss in any mode at all
    // (`_isBossUnlocked` / `defeatedBosses`) — the codex is a record of what
    // you have fought, and a Boss Challenge kill is a real fight. An act's
    // PROSE — its prologue and its ending — opens on the act itself, because
    // those are beats in a run, and every act's final boss is in
    // CHALLENGE_ROSTER (data/challengeBosses.js): gating them on a kill would
    // hand over an act's ending for one practice fight against its last boss.
    const cleared = (actId) => (this.save.statistics.clearedActs ?? []).includes(actId);

    // Prologue — always open.
    body.appendChild(this._section('Prologue'));
    body.appendChild(this._prose(PROLOGUE));

    // The Court — the seven-boss gauntlet, in order.
    body.appendChild(this._section('The Court'));
    BOSSES.forEach((boss, i) => {
      body.appendChild(this._entry(boss.name, BOSS_LORE[boss.spriteKey], this._isBossUnlocked(boss.spriteKey, i)));
    });
    // The Heralds — shared entry, unlocked by defeating any herald.
    body.appendChild(this._entry("The King's Heralds", BOSS_LORE.heralds, this._isBossUnlocked('heralds')));

    // Act II — the Undersong. Sealed until Act I has been cleared, the same bar
    // that unlocks the act itself (data/acts.js): the Act I epilogue's open
    // door is a beat the Archive shouldn't spoil for a player who hasn't opened
    // it yet. Any Act II progress counts too, so a player who is demonstrably
    // already down there never sees the section re-seal itself.
    body.appendChild(this._section('Act II · The Undersong'));
    const descended = cleared('act1')
      || ACT2_BOSSES.some((boss) => this._isBossUnlocked(boss.spriteKey))
      || this._isBossUnlocked('deepWardens');
    if (descended) {
      body.appendChild(this._prose(PROLOGUE_II));
    } else {
      body.appendChild(this._lockedProse('Something answers beneath the floor. Go down and see.'));
    }
    ACT2_BOSSES.forEach((boss) => {
      body.appendChild(this._entry(boss.name, BOSS_LORE[boss.spriteKey], this._isBossUnlocked(boss.spriteKey)));
    });
    // The Heralds of the Deep — shared entry, like the King's.
    body.appendChild(this._entry('The Heralds of the Deep', BOSS_LORE.deepWardens, this._isBossUnlocked('deepWardens')));

    // Act III — The Anthem. Sealed on having *cleared* Act II, because the whole
    // act is a reply to EPILOGUE_II and naming it earlier would spoil the ending
    // that earns it — the same bar data/acts.js gates the act on, exactly as
    // Act II's section is gated on Act I.
    body.appendChild(this._section('Act III · The Anthem'));
    const sang = cleared('act2') || ACT3_BOSSES.some((boss) => this._isBossUnlocked(boss.spriteKey));
    if (sang) {
      body.appendChild(this._prose(PROLOGUE_III));
    } else {
      body.appendChild(this._lockedProse('You have not finished the tale that starts this one.'));
    }
    ACT3_BOSSES.forEach((boss) => {
      body.appendChild(this._entry(boss.name, BOSS_LORE[boss.spriteKey], sang && this._isBossUnlocked(boss.spriteKey)));
    });
    body.appendChild(this._entry('The Heralds of the New Song', BOSS_LORE.songHeralds, this._isBossUnlocked('songHeralds')));

    // The Exclusives — challenge-only bosses.
    body.appendChild(this._section('The Exclusives'));
    CHALLENGE_BOSSES.forEach((boss) => {
      body.appendChild(this._entry(boss.name, BOSS_LORE[boss.spriteKey], this._isBossUnlocked(boss.spriteKey)));
    });

    // Champions — always open (your own roster).
    body.appendChild(this._section('Champions'));
    CHARACTERS.forEach((c) => {
      body.appendChild(this._entry(c.name, CHAMPION_LORE[c.id], true));
    });

    // Three endings, one per act, each gated on having CLEARED that act — the
    // same bar that unlocks the next one (data/acts.js). Not on a boss kill:
    // every act's final boss is in CHALLENGE_ROSTER, and an ending handed over
    // for one practice fight is an ending to a run the player never had.
    //
    // This used to be muddier still. Act I's epilogue doubled as the descent
    // interstitial and so opened on merely reaching the deep, and Act II's
    // opened on `normalWins`, which means "any clear" — a bar an Act I clear
    // now meets, which would have paid out the Undersong's ending for the King.
    body.appendChild(this._section('Epilogue'));
    if (cleared('act1')) {
      body.appendChild(this._prose(EPILOGUE));
    } else {
      body.appendChild(this._lockedProse('Unmake the King to unseal the end of his act.'));
    }

    body.appendChild(this._section('The First Silence'));
    if (cleared('act2')) {
      body.appendChild(this._prose(EPILOGUE_II));
    } else {
      body.appendChild(this._lockedProse('Descend to the first silence to unseal what waits there.'));
    }

    // And the one after it. Gated on the Anthem specifically, not on any Act
    // III progress — this is the answer to the act's own question, and reading
    // it before finishing would be reading the last page first.
    body.appendChild(this._section('The Last Verse'));
    if (cleared('act3')) {
      body.appendChild(this._prose(EPILOGUE_III));
    } else {
      body.appendChild(this._lockedProse('Hear out what you started.'));
    }

    this.el.appendChild(body);

    const hint = document.createElement('div');
    hint.className = 'scroll-hint';
    hint.textContent = '▼ Scroll to read';
    this.el.appendChild(hint);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);

    body.scrollTop = scrollPosition;
  }

  _section(label) {
    const el = document.createElement('div');
    el.className = 'lore-section-header';
    el.textContent = label;
    return el;
  }

  // A free-standing block of prose (prologue / epilogue). Preserves the
  // blank-line paragraph breaks in the source via white-space: pre-line.
  _prose(text) {
    const el = document.createElement('div');
    el.className = 'lore-prose';
    el.textContent = text;
    return el;
  }

  _lockedProse(text) {
    const el = document.createElement('div');
    el.className = 'lore-prose lore-locked';
    el.textContent = text;
    return el;
  }

  // A codex entry card — a boss or champion. Sealed entries hide the name and
  // body behind a placeholder so there's something to earn.
  _entry(name, lore, unlocked) {
    const card = document.createElement('div');
    card.className = `lore-entry${unlocked ? '' : ' lore-entry-locked'}`;

    const heading = document.createElement('div');
    heading.className = 'lore-entry-name';
    heading.textContent = unlocked ? name : '??? ??? ???';
    card.appendChild(heading);

    if (unlocked) {
      const epithet = document.createElement('div');
      epithet.className = 'lore-entry-epithet';
      epithet.textContent = lore.epithet;
      card.appendChild(epithet);

      const entry = document.createElement('div');
      entry.className = 'lore-entry-body';
      entry.textContent = lore.entry;
      card.appendChild(entry);
    } else {
      const sealed = document.createElement('div');
      sealed.className = 'lore-entry-epithet lore-locked';
      sealed.textContent = 'Sealed — defeat to unseal.';
      card.appendChild(sealed);
    }
    return card;
  }

  destroy() {
    this.el.remove();
  }
}
